'use client';
/**
 * `useClaimReceipt` — SDK-owned on-chain claim-receipt lookup.
 *
 * Not a manifest `queries:` HTTP entry — this is an on-chain
 * `getAccountInfo` against the claim-receipt PDA, so it ships as a
 * `source:`-defined query. The host supplies the per-network `Connection`
 * via `<TradingHostProvider value={{ ..., getConnection }}>`.
 */
import { useQuery } from '@tanstack/react-query';
import { useTradingHostContext } from './host-context.js';
export const CLAIM_RECEIPT_QUERY_KEY = 'claim-receipt';
/**
 * Check if a user has already claimed their prize for a market by looking
 * up the on-chain claim-receipt PDA.
 *
 * Seeds: [CLAIM_RECEIPT_SEED, market, user]
 */
export function useClaimReceipt(marketPubkey, userPubkey) {
    const { getActiveNetwork, getConnection } = useTradingHostContext();
    const activeNetwork = getActiveNetwork();
    const enabled = marketPubkey !== undefined && userPubkey !== undefined;
    const query = useQuery({
        queryKey: [CLAIM_RECEIPT_QUERY_KEY, marketPubkey, userPubkey, activeNetwork],
        queryFn: async () => {
            if (marketPubkey === undefined || userPubkey === undefined)
                return false;
            const connection = getConnection();
            const { deriveClaimReceiptPda } = await import('@morfi/contract-sdk');
            const { PublicKey } = await import('@solana/web3.js');
            const market = new PublicKey(marketPubkey);
            const user = new PublicKey(userPubkey);
            const [claimReceiptPda] = deriveClaimReceiptPda(market, user);
            const accountInfo = await connection.getAccountInfo(claimReceiptPda);
            return accountInfo !== null;
        },
        enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });
    return {
        hasClaimed: query.data === true,
        isLoading: query.isLoading,
        query,
    };
}
//# sourceMappingURL=use-claim-receipt.js.map